import {
  ADDRESS_BUILDING,
  ADDRESS_CITY, ADDRESS_COORDINATES,
  ADDRESS_COUNTRY, ADDRESS_POST_CODE, ADDRESS_STATE, ADDRESS_STREET,
  BUSINESS_UNIT_TYPE,
  CODE,
  DATE_CLOSED,
  DATE_OPENED,
  END_DATE,
  EQUITY_SHAREHOLDING,
  EU_TAXONOMY_CODE,
  FINANCIAL_CONTROL,
  NACE_CODE,
  NACE_SECTION,
  NAME,
  OWNERSHIP,
  PROFIT_CENTER_LINKS,
  START_DATE,
  UNIQUE_IDENTIFIER, UPSTREAM_TIER,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { DATA_SETS_DATE_FORMAT, VC_BOARD_TYPES } from 'constants/general';
import dayjs from 'dayjs';
import profitCenterLinksFormatted from 'utils/profitCenterLinksFormatted';

import getFormValue from '../getFormValue';
import getNaceLabel from '../getNaceLabel';

import type { IGetSupplierDefaultValues } from './interfaces/IGetSupplierDefaultValues';

function getSupplierDefaultValues({
  values, naceCodeTreeData, locationsCountries,
}: IGetSupplierDefaultValues) {
  const {
    name,
    code,
    addressBuilding,
    addressCity,
    addressCountry,
    addressPostCode,
    addressState,
    addressStreet,
    businessUnitType,
    dateClosed,
    dateOpened,
    naceCode,
    equityShareholding,
    euTaxonomyCode,
    financialControl,
    ownership,
    uniqueIdentifier,
    valueChainSection,
    upstreamTier,
    naceSection,
    addressCoordinates,
    profitCenterLinks,
    startDate,
    endDate,
  } = values || {};

  const defaultOption = undefined;

  const { naceSectionLabel, naceDvisions, naceCodeLabel } = getNaceLabel(
    { naceCode: naceCode || '', naceSection, naceCodeTreeData },
  );

  const locationsCountriesOptions = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return {
      value: country?.iso2 || countryCode,
      label: country?.name || countryCode,
    };
  };

  return {
    [NAME]: name || '',
    [CODE]: code || '',
    [UNIQUE_IDENTIFIER]: uniqueIdentifier || '',
    [VALUE_CHAIN_SECTION]: {
      value: valueChainSection?.split('. ')[1],
      label: valueChainSection?.split('. ')[1],
    },
    [VALUE_CHAIN_LIST]: {
      value: VC_BOARD_TYPES.UPSTREAM,
      label: VC_BOARD_TYPES.UPSTREAM,
    },
    [UPSTREAM_TIER]: upstreamTier ? {
      value: upstreamTier, label: upstreamTier,
    } : defaultOption,
    [OWNERSHIP]: ownership ? {
      value: ownership, label: ownership,
    } : defaultOption,
    [EQUITY_SHAREHOLDING]: getFormValue({ value: equityShareholding }),
    [FINANCIAL_CONTROL]: getFormValue({ value: financialControl }),
    [BUSINESS_UNIT_TYPE]: businessUnitType ? {
      value: businessUnitType, label: businessUnitType,
    } : defaultOption,
    [DATE_OPENED]: dateOpened ? dayjs(dateOpened).format(DATA_SETS_DATE_FORMAT) : '',
    [DATE_CLOSED]: dateClosed ? dayjs(dateClosed).format(DATA_SETS_DATE_FORMAT) : '',
    [START_DATE]: startDate ? dayjs(startDate).format(DATA_SETS_DATE_FORMAT) : '',
    [END_DATE]: endDate ? dayjs(endDate).format(DATA_SETS_DATE_FORMAT) : '',
    [NACE_CODE]: naceCode ? {
      value: naceCode,
      label: naceCodeLabel || '',
    } : defaultOption,
    [EU_TAXONOMY_CODE]: euTaxonomyCode || '',
    [NACE_SECTION]: naceSection ? {
      value: naceSection,
      label: naceSectionLabel || '',
      divisions: naceDvisions,
    } : defaultOption,
    [ADDRESS_COUNTRY]: addressCountry ? locationsCountriesOptions(addressCountry) : defaultOption,
    [ADDRESS_STATE]: addressState ? {
      value: addressState, label: addressState,
    } : defaultOption,
    [ADDRESS_POST_CODE]: addressPostCode || '',
    [ADDRESS_CITY]: addressCity ? {
      value: addressCity, label: addressCity,
    } : defaultOption,
    [ADDRESS_STREET]: addressStreet || '',
    [ADDRESS_BUILDING]: addressBuilding || '',
    [ADDRESS_COORDINATES]: addressCoordinates || '',
    [PROFIT_CENTER_LINKS]: profitCenterLinksFormatted(profitCenterLinks || []),
  };
}

export default getSupplierDefaultValues;
